import { useEffect, useState } from "react";
import { Anchor, CalendarDays, Check, Clock, Flame, MessageCircle, Music, Shield, Users } from "lucide-react";
import Seo from "../components/Seo";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import ChatWidget from "../components/ChatWidget";
import { Eyebrow, Reveal } from "../components/Reveal";
import { trackEvent } from "../analytics/ga4";
import API from "../api";

const WHATSAPP_URL = "https://wa.me/353862518252";
const INCLUDES = [
  { icon: Anchor, text: "Two nights' exclusive use of Collanmore Island Lodge" },
  { icon: Anchor, text: "Return high-speed ferry transfers from Rosmoney Pier" },
  { icon: Check, text: "Chinese banquet for up to 22 guests" },
  { icon: Flame, text: "Beach sauna and hot tub" },
  { icon: Shield, text: "The immersive Traitors Island mystery experience" },
];

const TRAILER_VERSION = "wide-1080"; // bump when the trailer file is replaced

const whatsappMessage = (weekendLabel) =>
  `Hi Ciarán, I'm interested in booking the Traitors Island all-inclusive weekend at Collanmore Island Lodge. My preferred dates are ${weekendLabel || "the February 2027 weekends"}. Could you please confirm availability and explain how to pay the €1,000 deposit? Thanks.`;

const whatsappLink = (weekendLabel) =>
  `${WHATSAPP_URL}?text=${encodeURIComponent(whatsappMessage(weekendLabel))}`;

const eventSchemas = (weekends) =>
  weekends.map((w) => ({
    "@context": "https://schema.org",
    "@type": "Event",
    name: `Traitors Island — ${w.label}`,
    description:
      "An all-inclusive immersive Traitors Island mystery weekend with exclusive use of Collanmore Island Lodge, return ferry transfers from Rosmoney Pier, Chinese banquet, beach sauna and hot tub.",
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    startDate: w.startDate,
    endDate: w.endDate,
    location: {
      "@type": "Place",
      name: "Collanmore Island Lodge",
      address: { "@type": "PostalAddress", addressLocality: "Clew Bay, Westport", addressRegion: "County Mayo", addressCountry: "IE" },
    },
    organizer: { "@type": "Organization", name: "Collanmore Island Lodge", url: "https://www.collanmoreislandlodge.com" },
    offers: {
      "@type": "Offer",
      price: "4999",
      priceCurrency: "EUR",
      availability: w.available ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
      url: "https://www.collanmoreislandlodge.com/traitors-island-experience/",
    },
  }));

export default function TraitorsPage() {
  const seo = { title: "Traitors Island 2027 | All-Inclusive Mystery Weekend Mayo", description: "All-inclusive Traitors Island mystery weekend at Collanmore Island Lodge, Clew Bay, Mayo — exclusive use, ferry transfers, Chinese banquet, sauna. February 2027, €4,999 for up to 22 guests.", path: "/traitors-island-experience/" };
  const [weekends, setWeekends] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", weekend_id: "", group_size: "12", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/api/traitors-weekends`)
      .then((r) => r.json())
      .then((d) => {
        setWeekends(d.weekends || []);
        setForm((f) => ({ ...f, weekend_id: f.weekend_id || (d.weekends || []).find((w) => w.available)?.id || "" }));
      })
      .catch(() => {});
  }, []);

  const selected = weekends.find((w) => w.id === form.weekend_id);
  const register = async (e) => {
    e.preventDefault();
    setError("");
    const size = Number(form.group_size);
    if (!Number.isInteger(size) || size < 1 || size > 22) {
      setError("Please enter a group size between 1 and 22");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/traitors-enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, group_size: size }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.[0]?.msg || data.detail || "Something went wrong");
      setDone(data);
      trackEvent("generate_lead", { occasion: "Traitors Island", guests: form.group_size, weekend: data.weekend_label });
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setSending(false);
    }
  };

  const scrollToRegister = (weekendId) => {
    if (weekendId) setForm((f) => ({ ...f, weekend_id: weekendId }));
    document.getElementById("register")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "Traitors Island — The Teaser",
    description:
      "An all-inclusive immersive Traitors Island mystery weekend with exclusive use of Collanmore Island Lodge, Clew Bay, County Mayo — February 2027.",
    thumbnailUrl: "https://www.collanmoreislandlodge.com/images/real/traitors-poster-midnight.webp",
    uploadDate: "2026-11-02",
    contentUrl: "https://www.collanmoreislandlodge.com/videos/traitors-trailer.mp4",
    publisher: { "@type": "Organization", name: "Collanmore Island Lodge", url: "https://www.collanmoreislandlodge.com" },
  };
  return (
    <div className="grain relative bg-[#0A0F1E]">
      <Seo {...seo} jsonLd={weekends.length ? [...eventSchemas(weekends), videoSchema] : [videoSchema]} />
      <Navbar ready />

      {/* Cinematic hero */}
      <section className="relative flex min-h-screen items-center justify-center overflow-hidden" data-testid="traitors-hero">
        <img
          src="/images/real/traitors-hero-aerial.webp"
          alt="Aerial view of Collanmore Island in Clew Bay, County Mayo, with Croagh Patrick behind"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/50 to-[#0A0F1E]" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-5 pb-24 pt-40 text-center md:px-10">
          <Reveal>
            <Eyebrow light>Collanmore Island Lodge presents</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-8 font-display text-5xl font-medium uppercase leading-[0.95] tracking-[0.06em] text-ivory md:text-8xl" data-testid="traitors-h1">
              Traitors <span className="text-gold italic normal-case tracking-normal">Island</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mx-auto mt-6 max-w-2xl text-base font-light leading-relaxed text-ivory/85 md:text-lg">
              An all-inclusive immersive mystery weekend on your own private island. Trust no one — February 2027.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <button
                type="button"
                onClick={() => scrollToRegister()}
                data-testid="traitors-hero-register"
                className="bg-gold px-10 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-black transition-colors duration-500 hover:bg-ivory"
              >
                Register Your Interest
              </button>
              <a
                href={whatsappLink(selected?.label)}
                target="_blank" rel="noopener noreferrer"
                data-testid="traitors-hero-whatsapp"
                className="flex items-center gap-2 border border-gold/60 px-10 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-gold transition-colors duration-500 hover:bg-gold hover:text-black"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp Ciarán
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Official teaser */}
      <section className="bg-black/50 py-16 md:py-24" data-testid="traitors-trailer-section">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <p className="text-center text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">Watch the teaser</p>
            <h2 className="mt-5 text-center font-display text-3xl font-medium text-ivory md:text-5xl" data-testid="traitors-trailer-heading">
              The mystery begins
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-10 border border-gold/25 bg-black p-3 md:p-4">
              <div className="flex justify-center">
                <video
                  src={`/videos/traitors-trailer.mp4?v=${TRAILER_VERSION}`}
                  controls
                  playsInline
                  preload="metadata"
                  poster="/images/real/traitors-poster-midnight.webp"
                  className="mx-auto block max-h-[78vh] w-auto max-w-full bg-black"
                  data-testid="traitors-trailer-player"
                />
              </div>
              <p className="px-2 pb-2 pt-4 text-center text-sm font-light text-ivory/70">
                One island. One group. Trust no one — February 2027.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Launch offer band */}
      <section className="bg-[#0A0F1E] py-14 md:py-20" data-testid="traitors-offer-band">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <Reveal>
            <div className="relative border-2 border-gold/60 bg-black/60 px-8 py-10 text-center md:px-14 md:py-12">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold px-4 py-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-black">
                Launch offer · First come, first served
              </span>
              <Music className="mx-auto h-8 w-8 text-gold" />
              <h2 className="mt-5 font-display text-2xl font-medium text-ivory md:text-4xl" data-testid="traitors-offer-heading">
                A two-man band plays for you — <span className="text-gold italic">free</span>
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-sm font-light leading-relaxed text-ivory/80 md:text-base" data-testid="traitors-offer-text">
                The first group to book gets a two-man band playing live in your own private bar in the lodge —
                an experience worth <strong className="font-medium text-gold">€500</strong>, included free with
                your weekend.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* The package */}
      <section className="py-20 md:py-28" data-testid="traitors-package">
        <div className="mx-auto grid max-w-[1200px] gap-14 px-5 md:px-10 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <Eyebrow light>The all-inclusive weekend</Eyebrow>
              <h2 className="mt-6 font-display text-3xl font-medium text-ivory md:text-5xl" data-testid="traitors-package-h2">
                Everything is taken care of
              </h2>
              <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-ivory/75 md:text-base">
                Arrive at Rosmoney Pier and let the island do the rest. One group at a time — the lodge, the island
                and the mystery are exclusively yours for two nights.
              </p>
            </Reveal>
            <div className="mt-10 flex flex-col gap-4" data-testid="traitors-includes">
              {INCLUDES.map(({ icon: Icon, text }) => (
                <Reveal key={text}>
                  <div className="flex items-start gap-4 border border-gold/20 bg-black/40 p-5">
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <p className="text-sm font-light leading-relaxed text-ivory/90 md:text-base">{text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div>
            <Reveal delay={0.1}>
              <div className="border border-gold/40 bg-black/60 p-8 md:p-12" data-testid="traitors-price">
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">Total package value</p>
                <p className="mt-2 font-display text-2xl text-ivory/50 line-through" data-testid="traitors-value">€5,576</p>
                <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">Your all-inclusive price</p>
                <p className="mt-2 font-display text-6xl font-medium text-gold md:text-7xl" data-testid="traitors-price-figure">€4,999</p>
                <p className="mt-4 flex items-center gap-3 text-sm font-light text-ivory/80" data-testid="traitors-price-capacity">
                  <Users className="h-4 w-4 text-gold" /> For up to 22 guests — one group only
                </p>
                <p className="mt-6 inline-block bg-gold/15 px-4 py-2 text-xs font-semibold uppercase tracking-widest2 text-gold" data-testid="traitors-saving">
                  Potential saving €577
                </p>
                <p className="mt-6 text-xs font-light leading-relaxed text-ivory/50">
                  Saving is €577 against the combined value of the standard package elements, subject to verification
                  of the normal package values.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Weekends */}
      <section className="bg-black/50 py-20 md:py-28" data-testid="traitors-weekends">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <Reveal>
            <Eyebrow light>Two weekends only</Eyebrow>
            <h2 className="mt-6 font-display text-3xl font-medium text-ivory md:text-5xl" data-testid="traitors-weekends-h2">
              Choose your weekend
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-2" data-testid="traitors-weekend-cards">
            {weekends.map((w, i) => (
              <Reveal key={w.id} delay={0.08 * i}>
                <div className="flex h-full flex-col border border-gold/25 bg-[#0A0F1E] p-8 md:p-10" data-testid={`traitors-weekend-card-${w.id}`}>
                  <span
                    className={`inline-flex w-fit items-center gap-2 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest2 ${w.available ? "bg-green-900/40 text-green-300" : "bg-red-900/40 text-red-300"}`}
                    data-testid={`traitors-availability-${w.id}`}
                  >
                    {w.available ? "Available" : "Fully booked"}
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-medium text-ivory md:text-3xl" data-testid={`traitors-weekend-label-${w.id}`}>
                    {w.label}
                  </h3>
                  <p className="mt-4 flex items-center gap-3 text-sm font-light text-ivory/70" data-testid={`traitors-deadline-${w.id}`}>
                    <Clock className="h-4 w-4 shrink-0 text-gold" /> Balance due by {w.balance_deadline}
                  </p>
                  <p className="mt-2 flex items-center gap-3 text-sm font-light text-ivory/70">
                    <Users className="h-4 w-4 shrink-0 text-gold" /> Up to 22 guests · €4,999 all-inclusive
                  </p>
                  <button
                    type="button"
                    onClick={() => scrollToRegister(w.id)}
                    disabled={!w.available}
                    data-testid={`traitors-register-button-${w.id}`}
                    className="mt-8 w-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-black transition-colors duration-500 hover:bg-ivory disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Register Your Interest
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Booking terms */}
      <section className="py-20 md:py-28" data-testid="traitors-terms">
        <div className="mx-auto grid max-w-[1200px] gap-12 px-5 md:px-10 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <Eyebrow light>Booking &amp; payment terms</Eyebrow>
            <h2 className="mt-6 font-display text-3xl font-medium text-ivory md:text-5xl" data-testid="traitors-terms-h2">
              How booking works
            </h2>
            <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-ivory/75 md:text-base">
              Bookings for Traitors Island are arranged personally with Ciarán Collins — there is no online payment
              for this promotion and no card details are taken on this website.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col gap-4" data-testid="traitors-terms-list">
              {[
                { icon: CalendarDays, text: "Booking deposit: €1,000, arranged directly with Ciarán Collins." },
                { icon: Check, text: "Remaining balance: €3,999, payable 10 weeks before arrival." },
                { icon: Clock, text: "5 February weekend — balance deadline 27 November 2026." },
                { icon: Clock, text: "26 February weekend — balance deadline 18 December 2026." },
                { icon: Shield, text: "Registering your interest does not constitute a confirmed booking." },
                { icon: Shield, text: "Bookings are only confirmed after availability has been agreed and the deposit has been received." },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-start gap-4 border border-ivory/15 p-5">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                  <p className="text-sm font-light leading-relaxed text-ivory/85 md:text-base">{text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Registration form */}
      <section id="register" className="bg-black/50 py-20 md:py-28" data-testid="traitors-register">
        <div className="mx-auto max-w-2xl px-5 md:px-10">
          {done ? (
            <div data-testid="traitors-thankyou">
              <Reveal>
                <Shield className="mx-auto h-12 w-12 text-gold" />
                <h2 className="mt-8 text-center font-display text-3xl font-medium text-ivory md:text-4xl">You&rsquo;re on the list</h2>
                <p className="mx-auto mt-6 max-w-xl text-center text-sm font-light leading-relaxed text-ivory/85 md:text-base" data-testid="traitors-thankyou-message">
                  Thank you for your interest in Traitors Island! To discuss availability, confirm your booking and
                  arrange your €1,000 deposit, please contact Ciarán Collins directly on WhatsApp.
                </p>
                <p className="mt-4 text-center text-xs font-light text-ivory/50">
                  A confirmation email is on its way to {form.email}. Preferred weekend: {done.weekend_label}.
                </p>
                <div className="mt-10 flex justify-center">
                  <a
                    href={whatsappLink(done.weekend_label)}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="traitors-whatsapp-button"
                    className="flex items-center gap-3 bg-[#25D366] px-10 py-5 text-sm font-semibold uppercase tracking-[0.22em] text-black shadow-[0_0_40px_rgba(37,211,102,0.35)] transition-transform duration-300 hover:scale-[1.03]"
                  >
                    <MessageCircle className="h-5 w-5" /> WhatsApp Ciarán to Book
                  </a>
                </div>
              </Reveal>
            </div>
          ) : (
            <Reveal>
              <Eyebrow light>Register your interest</Eyebrow>
              <h2 className="mt-6 font-display text-3xl font-medium text-ivory md:text-5xl" data-testid="traitors-form-h2">
                Be first in the game
              </h2>
              <p className="mt-5 text-sm font-light leading-relaxed text-ivory/75 md:text-base">
                Tell us who&rsquo;s coming and we&rsquo;ll hold your details. Registering costs nothing and commits you
                to nothing.
              </p>
              <form onSubmit={register} className="mt-10 grid gap-5" data-testid="traitors-form">
                <label className="grid gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-widest2 text-gold">Full name</span>
                  <input required minLength={2} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    data-testid="traitors-name-input" placeholder="Your full name"
                    className="border border-ivory/25 bg-black/40 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/30 focus:border-gold focus:outline-none" />
                </label>
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="grid gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-widest2 text-gold">Email address</span>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="traitors-email-input" placeholder="you@example.com"
                      className="border border-ivory/25 bg-black/40 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/30 focus:border-gold focus:outline-none" />
                  </label>
                  <label className="grid gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-widest2 text-gold">Mobile number</span>
                    <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="traitors-mobile-input" placeholder="+353 86 …"
                      className="border border-ivory/25 bg-black/40 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/30 focus:border-gold focus:outline-none" />
                  </label>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="grid gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-widest2 text-gold">Preferred weekend</span>
                    <select required value={form.weekend_id} onChange={(e) => setForm({ ...form, weekend_id: e.target.value })}
                      data-testid="traitors-weekend-select"
                      className="border border-ivory/25 bg-black/40 px-4 py-3.5 text-sm text-ivory focus:border-gold focus:outline-none">
                      {weekends.map((w) => (
                        <option key={w.id} value={w.id} disabled={!w.available} className="bg-[#0A0F1E]">
                          {w.label}{w.available ? "" : " — fully booked"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-widest2 text-gold">Estimated group size (1–22)</span>
                    <input required type="number" min={1} max={22} value={form.group_size}
                      onChange={(e) => setForm({ ...form, group_size: e.target.value === "" ? "" : Number(e.target.value) })}
                      data-testid="traitors-size-input" placeholder="e.g. 22"
                      className="border border-ivory/25 bg-black/40 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/30 focus:border-gold focus:outline-none" />
                  </label>
                </div>
                <label className="grid gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-widest2 text-gold">Message (optional)</span>
                  <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="traitors-message-input" placeholder="Anything Ciarán should know — occasion, if you'd like the whole lodge, questions…"
                    className="border border-ivory/25 bg-black/40 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/30 focus:border-gold focus:outline-none" />
                </label>
                {error && <p className="text-sm text-red-400" data-testid="traitors-form-error">{error}</p>}
                <button
                  type="submit" disabled={sending}
                  data-testid="traitors-form-submit"
                  className="mt-2 bg-gold px-10 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-black transition-colors duration-500 hover:bg-ivory disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Register Your Interest"}
                </button>
                <p className="text-xs font-light leading-relaxed text-ivory/50">
                  Registering your interest does not constitute a confirmed booking. Bookings are only confirmed
                  after availability has been agreed and the €1,000 deposit has been received.
                </p>
              </form>
            </Reveal>
          )}
        </div>
      </section>

      {/* Big WhatsApp band */}
      <section className="py-20 text-center md:py-28" data-testid="traitors-whatsapp-band">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <Reveal>
            <Eyebrow light>Ready to play?</Eyebrow>
            <h2 className="mt-6 font-display text-3xl font-medium text-ivory md:text-5xl">Speak to Ciarán directly</h2>
            <p className="mx-auto mt-5 max-w-xl text-sm font-light leading-relaxed text-ivory/75 md:text-base">
              Skip the form — message Ciarán Collins on WhatsApp and he&rsquo;ll confirm availability and arrange your
              €1,000 deposit personally.
            </p>
            <a
              href={whatsappLink(selected?.label)}
              target="_blank" rel="noopener noreferrer"
              data-testid="traitors-whatsapp-band-button"
              className="mt-10 inline-flex items-center gap-3 bg-[#25D366] px-12 py-6 text-sm font-semibold uppercase tracking-[0.22em] text-black shadow-[0_0_50px_rgba(37,211,102,0.4)] transition-transform duration-300 hover:scale-[1.03] md:text-base"
            >
              <MessageCircle className="h-6 w-6" /> WhatsApp Ciarán to Book
            </a>
            <p className="mt-6 text-xs font-light text-ivory/50">086 251 8252 · +353 86 251 8252</p>
          </Reveal>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
      <ChatWidget />

      {/* Mobile sticky WhatsApp bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 lg:hidden" data-testid="traitors-whatsapp-bar">
        <a
          href={whatsappLink(selected?.label)}
          target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-3 bg-[#25D366] py-4 text-xs font-semibold uppercase tracking-[0.22em] text-black"
        >
          <MessageCircle className="h-4 w-4" /> WhatsApp Ciarán to Book
        </a>
      </div>
    </div>
  );
}
